import { getPagination, transformOptions } from "@selectors";
import { getFormValues } from "redux-form";
import type { State } from "@store/reducers";
import { cloneObject } from "@utils/object";
const addAllOption = options => [{ label: "All", value: "" }].concat(options);

export const isLoading = (state: State) => {
  const clients = state.clients;
  for (const prop in clients) {
    if (clients[prop] && clients[prop].requestsInProgress > 0) return true;
  }
  return false;
};

export const getError = (state: State) => state.clients.error;
export const getSuccessMessage = (state: State) => state.clients.success;

// Client list
export const getClients = (state: State) => {
  return ((((state || {}).clients || {}).client || {}).list || {}).content || [];
};

export const getClientsSort = (state: State) =>
  (state.clients.client || {}).list.pagination.sort.orders;

export const getClientFilters = (state: State) => state.form["client-filter"]?.values || {};

export const getFilterOptions = (state: State) => {
  const schemaProps = ((state.clients.client || {}).schema || {}).properties;
  if (!schemaProps)
    return {
      accountManager: [],
      reseller: [],
      industry: [],
      status: [],
    };
  const depSectorOneOf =
    ((((state.clients.client || {}).schema || {}).dependencies || {}).sector || {}).oneOf || [];

  const industry1 =
    (((((depSectorOneOf[0] || {}).properties || {}).industry || {}).properties || {}).id || {})
      .oneOf || [];
  const industry2 =
    (((((depSectorOneOf[1] || {}).properties || {}).industry || {}).properties || {}).id || {})
      .oneOf || [];

  const industry = addAllOption(transformOptions(industry1.concat(industry2)));
  return {
    accountManager: addAllOption(transformOptions(schemaProps.accountManager.properties.id.oneOf)),
    reseller: addAllOption(transformOptions(schemaProps.reseller.properties.id.oneOf)),
    industry,
    status: addAllOption(transformOptions(schemaProps.status.oneOf)),
  };
};

export const getClientsPagination = (state: State): PaginationExtendedType => {
  return getPagination((state.clients.client || {}).list);
};

export const getClient = (state: State) => (state.clients.client || {}).current;

export const getClientsSchema = (state: State) => (state.clients.client || {}).schema;

export const getShowAddClientModal: boolean = (state: State) =>
  (state.clients.client || {}).modals.showAddModal;

export const getShowEditClientModal: boolean = (state: State) =>
  (state.clients.client || {}).modals.showEditModal;

//SLA
export const getActiveSlaModal = (state: State) =>
  (state.clients.agreements || {}).activeClientModal || null;
export const getSlaSchema = (state: State) => (state.clients.agreements || {}).schema;

//SLA Performance
export const getSlaPerformanceRequestsInProgress = (state: State) => {
  return (state.clients.slaPerformance || {}).requestsInProgress > 0;
};
export const getSlaPerformanceSectionsToggle = (state: State) => {
  return (state.clients.slaPerformance || {}).sectionsToggle;
};
export const getSlaPerformanceSelectedAndExcluded = (state: State) => {
  return (state.clients.slaPerformance || {}).areas;
};
export const getSlaPerformanceReportDetailsData = (state: State) => {
  return state.clients.slaPerformance;
};

// Client Service
export const getService = (state: State) => state.clients.service.service;

export const getServiceSettings = (state: State) =>
  (((state.clients || {}).service || {}).service || {}).settings;

export const getServiceSchema = (state: State) => ((state.clients || {}).service || {}).schema;

export const getMainOptionsSchema = (state: State) => {
  const originalSchema = getServiceSchema(state);
  const serviceSchema = cloneObject(originalSchema);
  if (!serviceSchema || serviceSchema.properties === null) return null;
  const originalSettings = originalSchema.properties.settings.properties;
  delete serviceSchema.properties.sections;
  delete serviceSchema.properties.settings;
  delete serviceSchema.properties.serviceConfigurationTemplate;

  return {
    ...serviceSchema,
    properties: {
      ...serviceSchema.properties,
      absenceLineNumber: originalSettings.absenceLineNumber,
      overflowSupport: originalSettings.overflowSupport,
      blockConsent: originalSettings.blockConsent,
      emailAppearAs: originalSettings.emailAppearAs,
      emailAddress: originalSettings.emailAddress,
    },
  };
};

export const getAdditionalOptionsSchema = (state: State) => {
  const serviceSchema = getServiceSchema(state);
  if (!serviceSchema || !serviceSchema.properties) return null;
  const settingsSchema = cloneObject(serviceSchema.properties.settings);
  delete settingsSchema.properties.blockConsent;
  delete settingsSchema.properties.emailAppearAs;
  delete settingsSchema.properties.overflowSupport;
  delete settingsSchema.properties.absenceLineNumber;
  delete settingsSchema.properties.emailAddress;

  return settingsSchema;
};

export const getAdminsSchema = (state: State) => state.clients.admin.schema;

export const getAdmins = (state: State) => state.clients.admin.list.content || [];

export const getEmployees = (state: State) => state.clients.employee.list.content;

export const getEmployeesSort = (state: State) =>
  state.employee.employee.list.pagination.sort.orders;

export const getChangelog = (state: State) => state.clients.changelog.list.content || [];

export const getChangelogPagination = (state: State): PaginationExtendedType =>
  getPagination(state.clients.changelog.list);

export const getChangesetFilterOptions = (state: State) => {
  return {
    serviceAdmin: addAllOption(
      transformOptions(
        (
          (
            (
              ((((state.clients.changeset || {}).schema || {}).properties || {}).changes || {})
                .items || {}
            ).properties || {}
          ).sectionKey || {}
        ).oneOf || [],
      ),
    ),
  };
};

// Changelog
export const getAgreements = (state: State) =>
  ((state.clients.agreements || {} || {}).list || {}).content || [];

export const getAgreementsChanges = (state: State) =>
  ((state.clients.agreements || {}).changes || {}).content || [];

export const getAgreementsPagination = (state: State): PaginationExtendedType =>
  getPagination((state.clients.agreements || {}).changes);

export const getShowAdditionalOptionsModal = (state: State): boolean =>
  (state.clients.client || {}).modals.showAdditionalOptionsModal;

export const getShowSelfServiceModal = (state: State): boolean =>
  (state.clients.client || {}).modals.showSelfServiceModal;

export const getShowMainOptionsModal = (state: State): boolean =>
  (state.clients.client || {}).modals.showMainOptionsModal;
